// Affiliate link utilities - provider-agnostic
// Configuration matches config/affiliate_providers.yml

interface Book {
  isbn?: string
  asin?: string
  bookshop_slug?: string
  title: string
  author: string
}

interface ProviderConfig {
  name: string
  display_name: string
  commission_rate: number
  identifier_field: string
  supports_search: boolean
  url_template: string
  search_template: string
  default_tag: string | null
  icon_color: string
  supports_indie?: boolean
}

interface AffiliateLinkResult {
  url: string
  provider: string
  provider_name: string
  icon_color: string
  supports_indie: boolean
}

// Provider configurations - keep in sync with config/affiliate_providers.yml
const PROVIDERS: Record<string, ProviderConfig> = {
  amazon: {
    name: "Amazon",
    display_name: "Amazon",
    commission_rate: 4.5,
    identifier_field: "asin",
    supports_search: true,
    url_template: "https://www.amazon.com/dp/{identifier}?tag={affiliate_tag}",
    search_template: "https://www.amazon.com/s?k={search_query}&tag={affiliate_tag}",
    default_tag: "booksforus946-20",
    icon_color: "#FF9900"
  },
  bookshop: {
    name: "Bookshop.org",
    display_name: "Bookshop.org",
    commission_rate: 10.0,
    identifier_field: "bookshop_slug",
    supports_search: true,
    url_template: "https://bookshop.org/a/{affiliate_tag}/{identifier}",
    search_template: "https://bookshop.org/search?keywords={search_query}&a={affiliate_tag}",
    default_tag: "books-for-us",
    icon_color: "#000000",
    supports_indie: true
  }
}

const DEFAULT_PROVIDER = "bookshop"

/**
 * Get the appropriate identifier for a book based on provider config
 */
function getIdentifier(book: Book, providerConfig: ProviderConfig): string | null {
  const identifierField = providerConfig.identifier_field as keyof Book

  // Try the preferred identifier field
  let identifier = book[identifierField] as string | undefined

  // For Amazon, ONLY use ISBN-10 (ASIN), never ISBN-13 in /dp/ URLs
  // ISBN-13 doesn't work reliably with /dp/
  if (providerConfig.name === "Amazon" && identifierField === "asin") {
    // Don't fall back to ISBN-13 for Amazon - we'll use search instead
    return identifier || null
  }

  // For other providers, fall back to ISBN if preferred field is empty
  if (!identifier && book.isbn) {
    identifier = book.isbn
  }

  return identifier || null
}

/**
 * Get the affiliate tag for a provider
 * In production, this could check environment variables or user preferences
 */
function getAffiliateTag(providerConfig: ProviderConfig): string | null {
  // For now, just use the default tag from config
  // In the future, this could check user preferences or environment
  return providerConfig.default_tag
}

/**
 * Generate an affiliate link for a book
 */
export function generateAffiliateLink(
  book: Book,
  provider: string = DEFAULT_PROVIDER
): AffiliateLinkResult | null {
  const providerConfig = PROVIDERS[provider]

  if (!providerConfig) {
    return null
  }

  const identifier = getIdentifier(book, providerConfig)
  const affiliateTag = getAffiliateTag(providerConfig)

  if (!affiliateTag) {
    return null
  }

  let url: string

  // If we have a direct identifier, use the direct URL template
  if (identifier) {
    url = providerConfig.url_template
      .replace("{identifier}", identifier)
      .replace("{affiliate_tag}", affiliateTag)
  }
  // Otherwise, fall back to search
  else if (providerConfig.supports_search) {
    const searchQuery = encodeURIComponent(`${book.title} ${book.author}`)
    url = providerConfig.search_template
      .replace("{search_query}", searchQuery)
      .replace("{affiliate_tag}", affiliateTag)
  }
  else {
    return null
  }

  return {
    url,
    provider,
    provider_name: providerConfig.display_name,
    icon_color: providerConfig.icon_color,
    supports_indie: providerConfig.supports_indie || false
  }
}

/**
 * Get all available providers
 */
export function getAvailableProviders(): string[] {
  return Object.keys(PROVIDERS).filter(provider => {
    const config = PROVIDERS[provider]
    return config.default_tag !== null
  })
}

/**
 * Get provider display info
 */
export function getProviderInfo(provider: string) {
  const config = PROVIDERS[provider]
  if (!config) return null

  return {
    name: config.display_name,
    icon_color: config.icon_color,
    supports_indie: config.supports_indie || false
  }
}
